import { WebhookProvider, WebhookStatus } from '@atlas/types'
import { CardShell, PaginationFooter, SectionCard } from '@atlas/ui-components'
import { Button } from '@atlas/ui-primitives'
import { useEffect, useState } from 'react'
import { useRetryWebhook, useWebhooks } from '@/hooks/useWebhooks'
import { apiErrorMessage } from '@/lib/api-error'
import { notification } from '@/notifications/notification-store'
import type { WebhookDTO } from '@/services/webhook.service'
import { WebhookTable } from './WebhookTable'

/**
 * SUPER_ADMIN diagnostics for inbound webhook deliveries.
 *
 * Exists because a delivery that fails intake records its reason in the ledger and
 * then goes silent — nothing surfaces it, so until now the answer to "why is there
 * no work order?" was a psql query. Defaults to FAILED for that reason; the other
 * statuses are one click away.
 *
 * Ops-facing, following `FileProcessingDiagnostics`'s precedent — the same shape of
 * problem (a DLQ'd job nobody can see) and deliberately not a polished end-user
 * surface.
 */

/** `null` = every status. Ordered so the one you almost always want is first. */
const STATUS_FILTERS: readonly { label: string; value: WebhookStatus | null }[] = [
	{ label: 'failed', value: WebhookStatus.FAILED },
	{ label: 'received', value: WebhookStatus.RECEIVED },
	{ label: 'processing', value: WebhookStatus.PROCESSING },
	{ label: 'processed', value: WebhookStatus.PROCESSED },
	{ label: 'all', value: null },
]

/**
 * Relative windows rather than a date picker: this page answers "what just
 * happened", and the ledger grows by a row per delivery. `null` means no filter.
 */
const TIME_WINDOWS: readonly { label: string; minutes: number | null }[] = [
	{ label: 'last hour', minutes: 60 },
	{ label: 'last 24 h', minutes: 1440 },
	{ label: 'last 7 days', minutes: 10_080 },
	{ label: 'any time', minutes: null },
]

/** Matches the endpoint's own default, so the first render asks for what it would send anyway. */
const PAGE_SIZE = 25

/** One row of filter chips. Each change resets paging, which the caller owns. */
function FilterBar({
	status,
	sinceMinutes,
	provider,
	onStatus,
	onWindow,
	onProvider,
}: {
	status: WebhookStatus | null
	sinceMinutes: number | null
	provider: string | null
	onStatus: (value: WebhookStatus | null) => void
	onWindow: (minutes: number | null) => void
	onProvider: (code: string | null) => void
}) {
	return (
		<div className="flex flex-wrap items-center gap-2">
			{STATUS_FILTERS.map((option) => (
				<Button
					key={option.label}
					size="sm"
					variant={status === option.value ? 'primary' : 'outline'}
					onPress={() => onStatus(option.value)}
				>
					{option.label}
				</Button>
			))}
			<span className="mx-2 h-5 w-px bg-ds-border" aria-hidden="true" />
			{TIME_WINDOWS.map((window) => (
				<Button
					key={window.label}
					size="sm"
					variant={sinceMinutes === window.minutes ? 'primary' : 'outline'}
					onPress={() => onWindow(window.minutes)}
				>
					{window.label}
				</Button>
			))}
			<span className="mx-2 h-5 w-px bg-ds-border" aria-hidden="true" />
			{[null, ...Object.values(WebhookProvider)].map((code) => (
				<Button
					key={code ?? 'any-provider'}
					size="sm"
					variant={provider === code ? 'primary' : 'outline'}
					onPress={() => onProvider(code)}
				>
					{code ?? 'any provider'}
				</Button>
			))}
		</div>
	)
}

export function WebhookDiagnosticsPage() {
	const [status, setStatus] = useState<WebhookStatus | null>(WebhookStatus.FAILED)
	const [sinceMinutes, setSinceMinutes] = useState<number | null>(1440)
	const [provider, setProvider] = useState<string | null>(null)
	const [page, setPage] = useState(1)
	const [expandedId, setExpandedId] = useState<string | null>(null)

	const query = useWebhooks({
		status: status ?? undefined,
		provider: provider ?? undefined,
		sinceMinutes: sinceMinutes ?? undefined,
		page,
		limit: PAGE_SIZE,
	})
	const retry = useRetryWebhook()

	// Every filter change resets to page 1 — otherwise a narrower filter strands the
	// user on an empty page 7 of a two-page result.
	const applyFilter = (change: () => void) => {
		change()
		setPage(1)
	}

	const onRetry = (row: WebhookDTO) => {
		retry.mutate(row.webhookId, {
			onSuccess: () =>
				notification.success(
					`Re-queued ${row.webhookId.slice(0, 8)}…. Retrying does not change the payload, so a delivery that failed on a bad value will fail again.`,
				),
			onError: (error) => notification.error(apiErrorMessage(error, 'Could not re-queue that delivery.')),
		})
	}

	const pagination = query.data?.pagination
	const totalPages = pagination?.totalPages ?? 1
	const total = pagination?.total ?? 0

	// A refresh can shrink the result under a high page — deliveries age out of the
	// window, or a retry succeeds — leaving "page 4 of 2" over an empty table. The
	// filter handlers already reset paging; this covers the refetch path they cannot.
	useEffect(() => {
		if (page > totalPages) setPage(totalPages)
	}, [page, totalPages])

	return (
		<div className="space-y-6">
			<div className="flex items-start justify-between">
				<div>
					<h1 className="font-semibold text-2xl">Webhook deliveries</h1>
					<p className="text-ds-text-muted">Inbound payloads and what became of them.</p>
				</div>
				<Button variant="outline" onPress={() => void query.refetch()} isDisabled={query.isFetching}>
					{query.isFetching ? 'Refreshing…' : 'Refresh'}
				</Button>
			</div>

			<SectionCard
				title="Filters"
				description={
					<>
						Showing {total} matching {total === 1 ? 'delivery' : 'deliveries'}.
					</>
				}
			>
				<FilterBar
					status={status}
					sinceMinutes={sinceMinutes}
					provider={provider}
					onStatus={(value) => applyFilter(() => setStatus(value))}
					onWindow={(minutes) => applyFilter(() => setSinceMinutes(minutes))}
					onProvider={(code) => applyFilter(() => setProvider(code))}
				/>
			</SectionCard>

			<CardShell>
				<WebhookTable
					rows={query.data?.data ?? []}
					isLoading={query.isLoading}
					isError={query.isError}
					expandedId={expandedId}
					onToggleExpanded={(id) => setExpandedId((current) => (current === id ? null : id))}
					onRetry={onRetry}
					retryingId={retry.isPending ? (retry.variables ?? null) : null}
				/>

				<PaginationFooter
					page={page}
					totalPages={totalPages}
					total={total}
					noun="delivery"
					nounPlural="deliveries"
					isFetching={query.isFetching}
					onPage={(next) => setPage(Math.min(Math.max(1, next), totalPages))}
				/>
			</CardShell>
		</div>
	)
}
